import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../config/supabase';
import { User } from '@supabase/supabase-js';

interface AdminUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'super_admin';
  is_active: boolean;
}

interface AdminAuthContextType {
  adminUser: AdminUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  
  // Track last auth token to avoid unnecessary reloads
  const lastAuthTokenRef = React.useRef<string | null>(null);
  const sessionCheckInProgressRef = React.useRef<boolean>(false);

  useEffect(() => {
    // Only check admin session if we're on an admin route
    const isAdminRoute = window.location.pathname.startsWith('/adminmrg2025');
    
    if (isAdminRoute) {
      // Check initial session with retry logic
      checkAdminSession();
    } else {
      // Not an admin route, just set loading to false
      setLoading(false);
    }

    // Listen for auth changes across tabs/windows
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      const currentIsAdminRoute = window.location.pathname.startsWith('/adminmrg2025');
      const currentToken = session?.access_token || null;
      
      console.log('Admin auth state change:', event, 'isAdminRoute:', currentIsAdminRoute, 'token:', currentToken?.slice(0, 10));
      
      if (event === 'SIGNED_IN' && session?.user && currentIsAdminRoute) {
        // Check if token has changed
        if (currentToken === lastAuthTokenRef.current) {
          console.log('Same token on SIGNED_IN, skipping reload');
          return;
        }
        lastAuthTokenRef.current = currentToken;
        await loadAdminProfile(session.user);
      } else if (event === 'SIGNED_OUT') {
        setAdminUser(null);
        localStorage.removeItem('admin_last_activity');
        localStorage.removeItem('admin_last_session_check');
        lastAuthTokenRef.current = null;
      } else if (event === 'TOKEN_REFRESHED' && session?.user && currentIsAdminRoute) {
        // Token refreshed - update reference but don't reload profile if we already have it
        console.log('Token refreshed');
        lastAuthTokenRef.current = currentToken;
        if (!adminUser) {
          await loadAdminProfile(session.user);
        }
      } else if (!currentIsAdminRoute) {
        // Not on admin route, don't try to load admin profile
        setAdminUser(null);
        setLoading(false);
      }
    });

    // Handle page visibility changes (when user switches tabs)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible again
        const isAdminRoute = window.location.pathname.startsWith('/adminmrg2025');
        if (isAdminRoute) {
          console.log('Page visible again, checking admin session...');
          checkAdminSession();
        }
      }
    };

    // Handle focus events (when user returns to the tab)
    const handleFocus = () => {
      const isAdminRoute = window.location.pathname.startsWith('/adminmrg2025');
      if (isAdminRoute) {
        console.log('Window focused, checking admin session...');
        checkAdminSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const checkAdminSession = async (retryCount = 0): Promise<void> => {
    try {
      // Prevent multiple simultaneous session checks
      if (sessionCheckInProgressRef.current) {
        console.log('Session check already in progress, skipping...');
        return;
      }

      // Only check admin session if we're on an admin route
      const isAdminRoute = window.location.pathname.startsWith('/adminmrg2025');
      
      if (!isAdminRoute) {
        setLoading(false);
        return;
      }

      sessionCheckInProgressRef.current = true;

      // Don't check too frequently - wait at least 2 seconds between checks
      const lastCheck = localStorage.getItem('admin_last_session_check');
      if (lastCheck) {
        const timeSinceLastCheck = Date.now() - parseInt(lastCheck);
        if (timeSinceLastCheck < 2000) {
          console.log('Skipping session check - too soon since last check');
          setLoading(false);
          sessionCheckInProgressRef.current = false;
          return;
        }
      }
      localStorage.setItem('admin_last_session_check', Date.now().toString());

      // Check if session has expired (24 hours of inactivity)
      const lastActivity = localStorage.getItem('admin_last_activity');
      if (lastActivity) {
        const lastActivityTime = parseInt(lastActivity);
        const now = Date.now();
        const hoursSinceLastActivity = (now - lastActivityTime) / (1000 * 60 * 60);
        
        // If more than 24 hours of inactivity, sign out
        if (hoursSinceLastActivity > 24) {
          console.log('Admin session expired after 24h of inactivity');
          await supabase.auth.signOut();
          localStorage.removeItem('admin_last_activity');
          localStorage.removeItem('admin_last_session_check');
          lastAuthTokenRef.current = null;
          setLoading(false);
          sessionCheckInProgressRef.current = false;
          return;
        }
      }

      // Update last activity time
      localStorage.setItem('admin_last_activity', Date.now().toString());

      // Get session with retry logic
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Error getting session:', sessionError);
        
        // Retry up to 3 times with exponential backoff
        if (retryCount < 3) {
          const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
          console.log(`Retrying session check in ${delay}ms (attempt ${retryCount + 1}/3)`);
          sessionCheckInProgressRef.current = false;
          await new Promise(resolve => setTimeout(resolve, delay));
          return checkAdminSession(retryCount + 1);
        }
        
        setLoading(false);
        sessionCheckInProgressRef.current = false;
        return;
      }
      
      if (session?.user) {
        const currentToken = session.access_token;
        
        // Check if token has changed - if same token, skip reload
        if (currentToken === lastAuthTokenRef.current && adminUser) {
          console.log('Same token and user already loaded, skipping profile reload');
          setLoading(false);
          sessionCheckInProgressRef.current = false;
          return;
        }
        
        // Update token reference
        lastAuthTokenRef.current = currentToken;
        
        await loadAdminProfile(session.user);
      } else {
        lastAuthTokenRef.current = null;
        setLoading(false);
      }
      
      sessionCheckInProgressRef.current = false;
    } catch (error) {
      console.error('Error checking admin session:', error);
      
      // Retry on error
      if (retryCount < 3) {
        const delay = Math.pow(2, retryCount) * 1000;
        console.log(`Retrying after error in ${delay}ms (attempt ${retryCount + 1}/3)`);
        sessionCheckInProgressRef.current = false;
        await new Promise(resolve => setTimeout(resolve, delay));
        return checkAdminSession(retryCount + 1);
      }
      
      setLoading(false);
      sessionCheckInProgressRef.current = false;
    }
  };

  const loadAdminProfile = async (user: User, retryCount = 0): Promise<void> => {
    // Prevent multiple simultaneous profile loads
    if (isLoadingProfile) {
      console.log('Profile load already in progress, skipping...');
      return;
    }

    setIsLoadingProfile(true);
    
    try {
      const { data: adminData, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        console.error('Error loading admin profile:', error);
        
        // Retry on database error
        if (retryCount < 3) {
          const delay = Math.pow(2, retryCount) * 1000;
          console.log(`Retrying admin profile load in ${delay}ms (attempt ${retryCount + 1}/3)`);
          await new Promise(resolve => setTimeout(resolve, delay));
          setIsLoadingProfile(false);
          return loadAdminProfile(user, retryCount + 1);
        }
        
        await supabase.auth.signOut();
        setAdminUser(null);
        setLoading(false);
        setIsLoadingProfile(false);
        return;
      }

      if (!adminData) {
        // User exists in auth but not in admin_users table
        console.log('User not found in admin_users table');
        await supabase.auth.signOut();
        setAdminUser(null);
        setLoading(false);
        setIsLoadingProfile(false);
        return;
      }

      setAdminUser({
        id: adminData.id,
        email: adminData.email,
        first_name: adminData.first_name,
        last_name: adminData.last_name,
        role: adminData.role || 'admin',
        is_active: adminData.is_active
      });
      setLoading(false);
      setIsLoadingProfile(false);
    } catch (error) {
      console.error('Unexpected error loading admin profile:', error);
      await supabase.auth.signOut();
      setAdminUser(null);
      setLoading(false);
      setIsLoadingProfile(false);
    }
  };

  const signIn = async (email: string, password: string): Promise<{ error?: string }> => {
    try {
      // DO NOT sign out - allow separate admin and provider sessions
      // First check if user exists in admin_users table
      const { data: adminData, error: adminError } = await supabase
        .from('admin_users')
        .select('user_id, email, is_active')
        .eq('email', email.toLowerCase())
        .eq('is_active', true)
        .maybeSingle();

      if (adminError) {
        console.error('Error checking admin user:', adminError);
        return { error: 'Erreur lors de la vérification du compte' };
      }

      if (!adminData) {
        return { error: 'Compte administrateur inexistant ou désactivé' };
      }

      // Sign in with Supabase Auth
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase(),
        password: password
      });

      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          return { error: 'Email ou mot de passe incorrect' };
        }
        return { error: error.message };
      }

      if (!data.user) {
        return { error: 'Erreur lors de la connexion' };
      }

      // DOUBLE CHECK: Vérifier que c'est bien un admin
      const { data: verifyAdmin, error: verifyError } = await supabase
        .from('admin_users')
        .select('*')
        .eq('user_id', data.user.id)
        .eq('is_active', true)
        .maybeSingle();

      if (verifyError || !verifyAdmin) {
        // Pas un admin, déconnecter immédiatement
        await supabase.auth.signOut();
        return { error: 'Accès refusé: Ce compte n\'est pas un compte administrateur' };
      }

      // Update last login and set last activity
      await supabase
        .from('admin_users')
        .update({ last_login_at: new Date().toISOString() })
        .eq('user_id', data.user.id);

      // Set last activity time for 24h session tracking
      localStorage.setItem('admin_last_activity', Date.now().toString());
      // Marquer comme session admin avec les nouvelles clés
      localStorage.setItem('admin_session', 'true');
      localStorage.setItem('admin_user_id', data.user.id);

      // Profile will be loaded by the auth state change listener
      return {};
    } catch (error: any) {
      console.error('Admin sign in error:', error);
      return { error: 'Une erreur est survenue' };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setAdminUser(null);
      localStorage.removeItem('admin_last_activity');
      localStorage.removeItem('admin_last_session_check');
      localStorage.removeItem('admin_session');
      localStorage.removeItem('admin_user_id');
      lastAuthTokenRef.current = null;
    } catch (error) {
      console.error('Admin sign out error:', error);
    }
  };

  const value = {
    adminUser,
    loading,
    signIn,
    signOut
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};